import { getSolReportMrtStyle } from "@/functions/getSolReportMrtStyle.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { useFetchLastTouchFilterOptions } from "@/queries/useFetchLastTouchFilterOptions.ts";
import {
    type MrtLastTouchReportCase,
    fetchAllLastTouchRecords,
    useFetchReportLastTouch,
} from "@/queries/useFetchReportLastTouch.ts";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Box, Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { download, generateCsv, mkConfig } from "export-to-csv";
import {
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_PaginationState,
    type MRT_Row,
    type MRT_SortingState,
    MaterialReactTable,
    useMaterialReactTable,
} from "material-react-table";
import { useSnackbar } from "notistack";
import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

const csvConfig = mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: true,
});

const REPORT_TYPE_MAP = {
    attorney: "Last Client Contact - Attorney",
    cm: "Last Client Contact - CM",
    all: "Last Client Contact - All Statuses",
} as const;

const REPORT_TYPE_OPTIONS = Object.values(REPORT_TYPE_MAP);

const getReportTypeLabel = (reportType: "attorney" | "cm" | "all" | ""): string =>
    reportType ? REPORT_TYPE_MAP[reportType] : "";

const ATTORNEY_STATUSES = [
    "Check Received",
    "Hold Harmless",
    "Litigation",
    "Mediation",
    "Negotiation",
    "Outsourced",
    "Policy Limits",
    "Top Offer",
];

const CM_STATUSES = [
    "Demand Complete",
    "Demand Draft",
    "Demand Ready",
    "Denial",
    "Done Treating",
    "Litigation Review",
    "New Client",
    "No Liability",
    "No UM",
    "No UM Exposure",
    "Not Authorized",
    "Pursuing UM",
    "Treating",
    "Withdrawal Pending",
];

const MrtLastTouchReportVirtualized = () => {
    const navigate = useNavigate();
    const fetch = useAuthenticatedFetch();
    const { enqueueSnackbar } = useSnackbar();
    const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "lastTouch", desc: false }]);
    const [pagination, setPagination] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 100,
    });
    const [selectedReportType, setSelectedReportType] = useState<"attorney" | "cm" | "all" | "">(
        "",
    );
    const [attorney, setAttorney] = useState("");
    const [caseManager, setCaseManager] = useState("");
    const [team, setTeam] = useState("");
    const [isExporting, setIsExporting] = useState(false);

    // Build column filters from dropdown selections
    const columnFilters = useMemo<MRT_ColumnFiltersState>(() => {
        const filters: MRT_ColumnFiltersState = [];
        if (attorney) {
            filters.push({ id: "assignedToAttorneyPretty", value: attorney });
        }
        if (caseManager) {
            filters.push({ id: "assignedToParalegalPretty", value: caseManager });
        }
        if (team) {
            filters.push({ id: "assignedTeam", value: team });
        }
        return filters;
    }, [attorney, caseManager, team]);

    // Fetch report data with server-side pagination
    const { data, isError, isRefetching, isLoading } = useFetchReportLastTouch({
        isQueryEnabled: selectedReportType !== "",
        reportType: selectedReportType === "" ? "all" : selectedReportType,
        columnFilters,
        pagination,
        sorting,
    });

    // Fetch filter options from full result set (not just current page)
    const { data: filterOptions } = useFetchLastTouchFilterOptions({
        reportType: selectedReportType || "all",
        enabled: !!selectedReportType,
    });

    const columns = useMemo<MRT_ColumnDef<MrtLastTouchReportCase>[]>(
        () => [
            {
                accessorKey: "id",
                header: "Case #",
                sortingFn: "alphanumeric",
                size: 110,
            },
            {
                accessorKey: "clientName",
                header: "Client Name",
            },
            {
                accessorKey: "caseType",
                header: "Type",
            },
            {
                accessorKey: "caseStatus",
                header: "Liability Status",
            },
            {
                accessorKey: "caseStatusUM",
                header: "UM Status",
            },
            {
                accessorKey: "caseName",
                header: "Important Notes",
            },
            {
                accessorKey: "assignedToParalegalPretty",
                header: "Case Manager",
            },
            {
                accessorKey: "lastTouch",
                header: "DSC",
                Cell: ({ row }) => {
                    if (row.original.lastTouch) {
                        const today = new Date();
                        return Math.floor(
                            (today.getTime() - row.original.lastTouch.getTime()) /
                                (1000 * 60 * 60 * 24),
                        );
                    }
                    return "";
                },
            },
            {
                accessorKey: "assignedToAttorneyPretty",
                header: "Attorney",
            },
            {
                accessorKey: "assignedTeam",
                header: "Team",
            },
        ],
        [],
    );

    const handleExportData = useCallback(async () => {
        if (!selectedReportType) {
            return;
        }

        setIsExporting(true);
        try {
            // Fetch ALL records for export, not just the current page
            const allData = await fetchAllLastTouchRecords(
                fetch,
                selectedReportType,
                columnFilters,
            );

            if (allData?.data) {
                const exportData = allData.data.map((datum) => {
                    return {
                        "Case #": datum.id,
                        "Client Name": datum.clientName,
                        Type: datum.caseType,
                        "Liability Status": datum.caseStatus,
                        "UM Status": datum.caseStatusUM,
                        "Important Notes": datum.caseName,
                        "Case Manager": datum.assignedToParalegalPretty,
                        DSC: datum.lastTouch?.toLocaleDateString(),
                        Attorney: datum.assignedToAttorneyPretty,
                        Team: datum.assignedTeam,
                    };
                });
                const csv = generateCsv(csvConfig)(exportData);
                download(csvConfig)(csv);
                enqueueSnackbar(`Exported ${allData.data.length} records`, { variant: "success" });
            }
        } catch (error) {
            enqueueSnackbar(error instanceof Error ? error.message : "Failed to export data", {
                variant: "error",
            });
        } finally {
            setIsExporting(false);
        }
    }, [selectedReportType, columnFilters, fetch, enqueueSnackbar]);

    const handleRowClick = (row: MRT_Row<MrtLastTouchReportCase>) => {
        navigate(`/case-detail/${row.original.id}`);
    };

    // Reset pagination when filters change
    useEffect(() => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, [selectedReportType, attorney, caseManager, team]);

    // Reset filters when report type changes
    useEffect(() => {
        setCaseManager("");
        setAttorney("");
        setTeam("");
    }, [selectedReportType]);

    const table = useMaterialReactTable({
        columns,
        data: selectedReportType === "" || isLoading || isError || !data?.data ? [] : data.data,

        initialState: {
            showColumnFilters: false,
            showGlobalFilter: false,
            density: "compact",
        },

        // top toolbar
        enableGlobalFilter: false,
        enableTopToolbar: true,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableHiding: false,
        enableColumnFilters: false,

        // server side pagination and sorting
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,

        onPaginationChange: setPagination,
        onSortingChange: setSorting,

        // state
        rowCount: data?.meta?.totalRowCount ?? 0,
        state: {
            isLoading,
            pagination,
            showAlertBanner: isError,
            showProgressBars: isRefetching,
            showLoadingOverlay: isLoading || isRefetching,
            sorting,
        },

        muiToolbarAlertBannerProps: isError
            ? {
                  color: "error",
                  children: "Error loading data",
              }
            : undefined,

        renderTopToolbarCustomActions: () => (
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                }}
            >
                <Button
                    onClick={handleExportData}
                    startIcon={<FileDownloadIcon />}
                    disabled={isExporting || !selectedReportType}
                >
                    {isExporting ? "Exporting..." : "Generate Spreadsheet"}
                </Button>
            </Box>
        ),

        enablePagination: true,
        enableBottomToolbar: true,
        muiTableContainerProps: { sx: { maxHeight: "600px" } },
        muiTableBodyRowProps: ({ row }: { row: MRT_Row<MrtLastTouchReportCase> }) => ({
            onClick: () => handleRowClick(row),
            sx: {
                cursor: "pointer",
                "& .MuiTableCell-root": {
                    ...getSolReportMrtStyle({
                        caseStatus: row.original.caseStatus ?? "",
                        dateOfBirth: row.original.idPeople?.dateofbirth ?? null,
                    }),
                },
            },
        }),
    });

    return (
        <Stack
            sx={{
                flexGrow: 1,
            }}
        >
            <Stack
                sx={{
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <TextField
                    select
                    value={getReportTypeLabel(selectedReportType)}
                    name={"status"}
                    label={"Report Type"}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const value = event.target.value;
                        const entry = Object.entries(REPORT_TYPE_MAP).find(
                            ([, label]) => label === value,
                        );
                        setSelectedReportType((entry?.[0] as "attorney" | "cm" | "all") ?? "");
                    }}
                    sx={{
                        my: 1,
                        maxWidth: "300px",
                        minWidth: "300px",
                    }}
                >
                    <MenuItem key={"status-blank"} value={""}>
                        &nbsp;
                    </MenuItem>
                    {REPORT_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={`status-${option}`} value={`${option}`}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>

                {selectedReportType && (
                    <Typography
                        variant="body2"
                        mb={2}
                        sx={{ color: "#1c881a", textAlign: "center" }}
                    >
                        {selectedReportType === "attorney" &&
                            `Showing Statuses: ${ATTORNEY_STATUSES.join(", ")}`}
                        {selectedReportType === "cm" &&
                            `Showing Statuses: ${CM_STATUSES.join(", ")}`}
                        {selectedReportType === "all" && "Showing Statuses: All Statuses"}
                    </Typography>
                )}
            </Stack>
            <Stack
                sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                    gap: 2,
                }}
            >
                <TextField
                    select
                    value={attorney}
                    name={"attorney"}
                    label={"Attorney"}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setAttorney(event.target.value);
                    }}
                    sx={{
                        my: 1,
                        width: "100%",
                    }}
                >
                    <MenuItem key={"attorney-all"} value={""}>
                        All
                    </MenuItem>
                    {/* CAM-392: Blank option for truly unassigned (avoids conflict with attorneys named "Unassigned") */}
                    {filterOptions?.hasNullAttorney && (
                        <MenuItem key={"attorney-unassigned"} value={"__NULL__"}>
                            &nbsp;
                        </MenuItem>
                    )}
                    {filterOptions?.attorneys.map((option) => (
                        <MenuItem key={`attorney-${option}`} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    value={caseManager}
                    name={"caseManager"}
                    label={"Case Manager"}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setCaseManager(event.target.value);
                    }}
                    sx={{
                        my: 1,
                        width: "100%",
                    }}
                >
                    <MenuItem key={"case-manager-all"} value={""}>
                        All
                    </MenuItem>
                    {/* CAM-392: Blank option for truly unassigned */}
                    {filterOptions?.hasNullCaseManager && (
                        <MenuItem key={"case-manager-unassigned"} value={"__NULL__"}>
                            &nbsp;
                        </MenuItem>
                    )}
                    {filterOptions?.caseManagers.map((option) => (
                        <MenuItem key={`case-manager-${option}`} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    value={team}
                    name={"team"}
                    label={"Team"}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        setTeam(event.target.value);
                    }}
                    sx={{
                        my: 1,
                        width: "100%",
                    }}
                >
                    <MenuItem key={"team-all"} value={""}>
                        All
                    </MenuItem>
                    {/* CAM-392: Blank option for truly unassigned */}
                    {filterOptions?.hasNullTeam && (
                        <MenuItem key={"team-unassigned"} value={"__NULL__"}>
                            &nbsp;
                        </MenuItem>
                    )}
                    {filterOptions?.teams.map((option) => (
                        <MenuItem key={`team-${option}`} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
            <MaterialReactTable table={table} />
        </Stack>
    );
};

export default MrtLastTouchReportVirtualized;
